import * as React from 'react'
import {FC, useEffect, useRef, useState} from 'react'
import {observer} from 'mobx-react'
import cn from 'classnames'
import {ResizeObserver} from 'resize-observer'
import {each, reduce} from 'lodash'
import {
  CreditCardPaymentMethods,
  isCreditCardPaymentMethod,
  PaymentMethod,
} from '@raisenow/epik'
import {PaymentForm} from '../../../../lib/forms/PaymentForm'
import {SubscriptionRenewForm} from '../../../../lib/forms/SubscriptionRenewForm'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'

///////////////////////////////////////////////////////////////////////////////

type PaymentMethodDetailsHeaderInfoProps = {
  form: PaymentForm | SubscriptionRenewForm
}

type IconGroups = {
  [key in IconGroup]?: string[]
}

enum IconGroup {
  STATIC = 'static',
  ANIMATED = 'animated',
}

///////////////////////////////////////////////////////////////////////////////

const sortPaymentMethods = (
  paymentMethods: PaymentMethod[],
  priorityCreditCardTypes: PaymentMethod[],
): PaymentMethod[] => {
  const comparator = (a, b) => {
    const aIdx = priorityCreditCardTypes.indexOf(a)
    const bIdx = priorityCreditCardTypes.indexOf(b)

    const aIsPrio = aIdx !== -1
    const bIsPrio = bIdx !== -1

    if (aIsPrio && !bIsPrio) {
      return -1
    }
    if (!aIsPrio && bIsPrio) {
      return 1
    }
    if (aIsPrio && bIsPrio) {
      if (aIdx < bIdx) {
        return -1
      }
      if (aIdx > bIdx) {
        return 1
      }
    }
    return 0
  }

  return paymentMethods
    .filter((item) => item !== PaymentMethod.CREDENCIAL) // no svg for credencial for now (not provided in payu list of cc) but we have it in regexp list
    .sort(comparator)
}

const getAnimationStartIdx = (width: number) => (width < 420 ? 0 : 3)

///////////////////////////////////////////////////////////////////////////////

export const PaymentMethodDetailsHeaderInfo: FC<PaymentMethodDetailsHeaderInfoProps> = observer(
  (props) => {
    const {form} = props
    const {api} = useStores<{api: WidgetApi}>()
    const currentPaymentMethod = form.getPaymentMethod()
    const ref = useRef(null)
    const [animationStartIdx, setAnimationStartIdx] = useState(0)

    useEffect(() => {
      let resizeObserver
      const tamaroContainer = document.getElementById('tamaro-widget')

      if (tamaroContainer) {
        resizeObserver = new ResizeObserver(() => {
          setAnimationStartIdx(
            getAnimationStartIdx(tamaroContainer.clientWidth),
          )
        })

        resizeObserver.observe(tamaroContainer)
      }

      return () => {
        if (resizeObserver && tamaroContainer) {
          resizeObserver.unobserve(tamaroContainer)
        }
      }
    }, [])

    useEffect(() => {
      let interval

      if (ref.current) {
        const icons = [...ref.current.children] as HTMLElement[]
        const iconsCount = icons.length

        if (iconsCount === 1) {
          icons[0]?.classList.add('cc-icon-current')
        }

        if (iconsCount > 1) {
          let idx = 1

          interval = setInterval(() => {
            each(icons, (icon) => {
              icon.classList.remove('cc-icon-current')
            })

            icons[idx]?.classList.add('cc-icon-current')

            if (idx >= iconsCount - 1) {
              idx = 0
            } else {
              idx++
            }
          }, 1500)
        }
      }

      return () => clearInterval(interval)
    })

    if (!isCreditCardPaymentMethod(currentPaymentMethod)) {
      return null
    }

    const noop = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }

    const paymentMethods = form
      .getPossiblePaymentMethods()
      .filter((pm) => pm !== PaymentMethod.CREDIT_CARD)
      .filter((pm) => pm !== PaymentMethod.STRIPE)
      .filter((pm) => CreditCardPaymentMethods.indexOf(pm) !== -1)

    const priorityCreditCardTypes = api.resolveConfig<PaymentMethod>(
      'priorityCreditCardTypes',
    )
    const sortedPaymentMethods = sortPaymentMethods(
      paymentMethods,
      priorityCreditCardTypes,
    )

    let icons: string[]

    if (paymentMethods.includes(currentPaymentMethod)) {
      icons = [currentPaymentMethod]
    } else {
      icons = sortedPaymentMethods
    }

    let iconGroups = reduce<string, IconGroups>(
      icons,
      (res, pm, idx) => {
        res.static = res.static || []
        res.animated = res.animated || []

        if (idx >= animationStartIdx) {
          res.animated = [...res.animated, pm]
        } else {
          res.static = [...res.static, pm]
        }

        return res
      },
      {},
    )

    return (
      <div className="cc-icons">
        {Object.entries(iconGroups).map(([name, icons]) => {
          if (icons.length === 0) {
            return null
          }

          const contClassNames = cn([
            {'cc-icons-static': name === IconGroup.STATIC},
            {'cc-icons-animated': name === IconGroup.ANIMATED},
          ])

          let contProps = {}

          if (name === IconGroup.ANIMATED) {
            contProps = {ref}
          }

          return (
            <div
              key={name}
              className={contClassNames}
              onClick={noop}
              {...contProps}
            >
              {icons.map((pm, idx) => {
                const iconClassNames = cn([
                  'cc-icon',
                  `cc-icon-bg-image-${pm}`,
                  {'cc-icon-current': name === IconGroup.ANIMATED && idx === 0},
                ])

                return <div key={pm} className={iconClassNames} />
              })}
            </div>
          )
        })}
      </div>
    )
  },
)
